import _ from 'lodash';
import moment from 'moment';
import Highcharts from 'highcharts';
import Highstock from 'highcharts/highstock';

import { rgba, THEME } from '@miotech/picasso';

import { W } from '../i18n';
import { Theme } from '../types';
import themes from './themes';

const NAVIGATOR_HEIGHT = 48;
const CHART_PADDING_BOTTOM = 24;

const lineGraphCommon = {
  animation: false,
  lineWidth: 1,
  showInNavigator: true,
  marker: {
    symbol: 'circle',
    enabled: false,
    states: {
      hover: {
        enabled: true,
        lineColor: 'transparent',
      },
    },
  },
  states: {
    hover: {
      lineWidthPlus: 0,
      halo: {
        size: 8,
        opacity: 0.25,
      },
    },
  },
  // make sure Y is scaled between current min and max
  // threshold: null,
  // make sure Y starts at 0
  softThreshold: true,
};

type HighchartsComplexType = Highcharts.Options;

export function getHighchartsConfig({
  config,
  theme = window.__config__.theme,
  isHighstock = false,
  isPrint = false,
}: {
  config: Highcharts.Options,
  theme?: Theme,
  isHighstock?: boolean,
  isPrint?: boolean,
}): HighchartsComplexType {
  const usingTheme = theme || Theme.DARK;
  const colors = themes[usingTheme];
  const seriesColors = colors?.colors ?? themes.dark.colors;

  const baseConfig: Highcharts.Options = {
    colors: seriesColors,
    title: {
      text: undefined,
      style: {
        color: colors.primary,
      },
    },
    chart: {
      backgroundColor: 'transparent',
      style: {
        fontFamily:
          'Roboto, "Helvetica Neue For Number", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif',
      },
    },
    exporting: {
      enabled: false,
      chartOptions: {
        chart: {
          backgroundColor: colors.background,
        },
      },
    },
    credits: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    plotOptions: {
      area: {
        ...lineGraphCommon,
      },
      line: {
        ...lineGraphCommon,
      },
      column: {
        animation: false,
        borderWidth: 0,
      },
      pie: {
        animation: false,
        borderWidth: 0,
        point: {
          events: {
            legendItemClick: function handleClickFunc() {
              return false;
            },
          },
        },
      },
      map: {
        animation: false,
        borderColor: 'transparent',
        nullColor: colors.fifth,
      },
      series: {
        events: {
          legendItemClick: function handleClickFunc() {
            return false;
          },
        },
      },
    },
    xAxis: {
      labels: {
        style: {
          color: colors.secondary,
          fontSize: '12px',
        },
      },
      lineColor: colors.fourth,
      tickLength: 0,
      // crosshair: {
      //   color: colors.secondary,
      // },
      gridLineColor: colors.fourth,
      gridLineDashStyle: 'ShortDash',
    },
    yAxis: {
      labels: {
        style: {
          color: colors.secondary,
        },
      },
      gridLineColor: colors.fourth,
      gridLineDashStyle: 'ShortDash',
      opposite: false,
      tickLength: 0,
      lineColor: colors.fourth,
      lineWidth: 1,
      // crosshair: {
      //   width: 0,
      // },
      title: {
        text: null,
        style: {
          color: colors.primary,
        },
      },
      // allowDecimals: false,
    },
    legend: {
      // enabled: true,
      itemStyle: {
        cursor: 'default',
        color: colors.primary,
      },
      itemHoverStyle: {
        color: colors.primary,
      },
    },
    tooltip: {
      enabled: true,
      // shared: true,
      // split: false,
      backgroundColor: colors.fifth,
      borderColor: rgba(colors.secondary, 0.15),
      borderRadius: 8,
      // headerFormat: '',
      // pointFormat: '<span style="color:{point.color}">{series.name}</span>: <b>{point.y}</b><br/>',
      followPointer: true,
      followTouchMove: true,
      dateTimeLabelFormats: {
        millisecond: '%Y-%m-%d, %H:%M:%S.%L',
        second: '%Y-%m-%d, %H:%M:%S',
        minute: '%Y-%m-%d, %H:%M',
        hour: '%Y-%m-%d, %H',
        day: '%Y-%m-%d',
        week: 'Week from %A, %Y-%m-%d',
        month: '%Y-%m',
        year: '%Y',
      },
      valueDecimals: 2,
      style: {
        color: colors.primary,
        fontFamily:
          'Roboto, "Helvetica Neue For Number", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif',
      },
    },

    // highstock
    rangeSelector: {
      buttonTheme: {
        fill: 'transparent',
        stroke: colors.third,
        'stroke-width': 1,
        height: 20,
        width: 52,
        r: 12,
        style: {
          color: colors.secondary,
          'dominant-baseline': 'middle',
        },
        states: {
          hover: {
            stroke: colors.brand,
            fill: 'transparent',
            style: {
              color: colors.brand,
            },
          },
          select: {
            fill: colors.brand,
            stroke: colors.brand,
            style: {
              color: colors.white,
            },
          },
          disabled: {
            stroke: colors.fourth,
            fill: 'transparent',
            style: {
              color: colors.fourth,
            },
          },
        },
      },
      buttonSpacing: 16,
      labelStyle: {
        color: colors.secondary,
        'dominant-baseline': 'middle',
      },
      inputDateFormat: '%Y-%m-%d',
      selected: 2,
      buttons: [
        {
          type: 'month',
          count: 1,
          text: W('shared.highcharts.1m'),
        },
        {
          type: 'month',
          count: 3,
          text: W('shared.highcharts.3m'),
        },
        {
          type: 'month',
          count: 6,
          text: W('shared.highcharts.6m'),
        },
        {
          type: 'ytd',
          text: W('shared.highcharts.ytd'),
        },
        {
          type: 'year',
          count: 1,
          text: W('shared.highcharts.1y'),
        },
        {
          type: 'all',
          text: W('shared.highcharts.all'),
        },
      ],
      // date select
      inputBoxBorderColor: colors.third,
      inputStyle: {
        color: colors.primary,
        background: colors.fifth,
      },
    },
    navigator: {
      margin: 8,
      height: NAVIGATOR_HEIGHT,
      outlineColor: colors.third,
      maskFill: rgba(colors.background, 0.8),
      maskInside: false,
      xAxis: {
        labels: {
          enabled: true,
          y: 16,
          style: {
            color: colors.secondary,
            fontSize: '12px',
          },
          formatter: function formatterFunc() {
            return moment(Number(this.value)).format('YYYY');
          },
        },
        gridLineWidth: 0,
        minTickInterval: moment.duration(1, 'years').asMilliseconds(),
      },
      series: {
        type: 'line',
      },
      handles: {
        width: 5,
        height: 12,
        backgroundColor: colors.fifth,
        borderColor: colors.third,
      },
    },
  };

  const highstockConfig: Highstock.Options = {
    chart: {
      spacingBottom: CHART_PADDING_BOTTOM,
    },
    xAxis: {
      tickPositioner() {
        const now = moment(Date.now());
        const start = moment(Number(this.min));
        const end = moment(Number(this.max));

        const yearInMilliseconds = moment.duration(1, 'years').asMilliseconds();
        const monthInMilliseconds = moment
          .duration(1, 'months')
          .asMilliseconds();
        const dayInMilliseconds = moment.duration(1, 'days').asMilliseconds();

        let durationInMilliseconds = dayInMilliseconds;

        if (end.year() - start.year() >= 5) {
          durationInMilliseconds = yearInMilliseconds;
        } else if (start.year() < now.year() || start.year() !== end.year()) {
          durationInMilliseconds = monthInMilliseconds;
        }

        return _.uniq(
          (this.tickPositions || [])
            .map(t => Math.floor(t / durationInMilliseconds))
            .map(t => t * durationInMilliseconds),
        );
      },
      labels: {
        formatter() {
          const now = moment(Date.now());
          const start = moment(Number(this.axis.min));
          const end = moment(Number(this.axis.max));
          const time = moment(this.value);

          if (end.year() - start.year() >= 5) {
            return time.format('YYYY');
          }
          if (start.year() < now.year() || start.year() !== end.year()) {
            return time.format('YYYY-MM');
          }
          return time.format('MM-DD');
        },
      },
      crosshair: {
        color: colors.brand,
      },
      lineWidth: 1,
      lineColor: colors.third,
    },
  };

  const printConfig = {
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      series: {
        enableMouseTracking: false,
      },
    },
    legend: {
      itemStyle: {
        cursor: 'default',
      },
    },
  };

  if (isHighstock) {
    _.merge({}, baseConfig, highstockConfig);
  }

  if (isPrint) {
    _.merge({}, baseConfig, printConfig);
  }

  return _.merge({}, baseConfig, {
    ...config,
    series: (config.series || []).map((item, index) => {
      if (_.get(config, 'chart.type') === 'pie') {
        const tempItem = item as Highcharts.SeriesPieOptions;
        return {
          ...item,
          data: (tempItem.data || []).map((point, pointIndex) => {
            const _colors = THEME.sliceList || seriesColors;
            const color = _colors[pointIndex % _colors.length];
            if (typeof point === 'object' && !Array.isArray(point)) {
              const renderColor = point?.color || color;
              return {
                ...point,
                color: renderColor,
              };
            }
            return point;
          }),
        };
      }

      const color = seriesColors[index % seriesColors.length];
      const renderColor = item.color || color;
      return {
        ...item,
        color: renderColor,
        // @ts-ignore
        fillColor: item.fillColor || {
          linearGradient: [0, 0, 0, 400],
          stops: [
            [0, rgba(renderColor.toString(), 0.05)],
            [0.6, rgba(renderColor.toString(), 0)],
          ],
        },
        // @ts-ignore
        negativeFillColor: item.negativeFillColor || {
          linearGradient: [0, 0, 0, 400],
          stops: [
            [0, rgba(renderColor.toString(), 0)],
            [0.6, rgba(renderColor.toString(), 0.05)],
          ],
        },
      };
    }),
  });
}

export function getHighchartsOptions(config = {}) {
  const baseConfig = {
    lang: {
      rangeSelectorZoom: W('shared.highcharts.zoom'),
      rangeSelectorFrom: W('shared.highcharts.from'),
      rangeSelectorTo: W('shared.highcharts.to'),
      // [ "k" , "M" , "G" , "T" , "P" , "E"]
      numericSymbols: [
        W('shared.highcharts.kilo'),
        W('shared.highcharts.mega'),
        W('shared.highcharts.giga'),
        W('shared.highcharts.tera'),
        W('shared.highcharts.peta'),
        W('shared.highcharts.exa'),
      ],
      weekdays: [
        W('shared.highcharts.sunday'),
        W('shared.highcharts.monday'),
        W('shared.highcharts.tuesday'),
        W('shared.highcharts.wednesday'),
        W('shared.highcharts.thursday'),
        W('shared.highcharts.friday'),
        W('shared.highcharts.saturday'),
      ],
      months: [
        W('shared.highcharts.january'),
        W('shared.highcharts.february'),
        W('shared.highcharts.march'),
        W('shared.highcharts.april'),
        W('shared.highcharts.may'),
        W('shared.highcharts.june'),
        W('shared.highcharts.july'),
        W('shared.highcharts.august'),
        W('shared.highcharts.september'),
        W('shared.highcharts.october'),
        W('shared.highcharts.november'),
        W('shared.highcharts.december'),
      ],
      shortMonths: [
        W('shared.highcharts.january.short'),
        W('shared.highcharts.february.short'),
        W('shared.highcharts.march.short'),
        W('shared.highcharts.april.short'),
        W('shared.highcharts.may.short'),
        W('shared.highcharts.june.short'),
        W('shared.highcharts.july.short'),
        W('shared.highcharts.august.short'),
        W('shared.highcharts.september.short'),
        W('shared.highcharts.october.short'),
        W('shared.highcharts.november.short'),
        W('shared.highcharts.december.short'),
      ],
    },
  };

  return _.merge({}, baseConfig, config);
}
